import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import {
  ArrowLeft, User, Users, DollarSign, Phone, Home, Truck,
  Hash, CreditCard, GraduationCap, Calendar, Shield,
} from 'lucide-react';

// ─── Billing months (Apr–Mar) ──────────────────────────────────────────────────
const BILLING_MONTHS = [
  { key:'apr', name:'April 2026',    num:4  },
  { key:'may', name:'May 2026',      num:5  },
  { key:'jun', name:'June 2026',     num:6  },
  { key:'jul', name:'July 2026',     num:7  },
  { key:'aug', name:'August 2026',   num:8  },
  { key:'sep', name:'September 2026',num:9  },
  { key:'oct', name:'October 2026',  num:10 },
  { key:'nov', name:'November 2026', num:11 },
  { key:'dec', name:'December 2026', num:12 },
  { key:'jan', name:'January 2027',  num:1  },
  { key:'feb', name:'February 2027', num:2  },
  { key:'mar', name:'March 2027',    num:3  },
];

// ─── Ledger calculation (mirrors FeesPage logic) ───────────────────────────────
function buildLedger(student, feeComponents, transportRoutes, payments) {
  const resolvedComponents = [];
  const names = Array.from(new Set(feeComponents.map(c => c.name?.toLowerCase())));
  names.forEach(n => {
    const custom  = feeComponents.find(c => c.name?.toLowerCase() === n && c.classId === student.classId);
    const generic = feeComponents.find(c => c.name?.toLowerCase() === n && c.classId === 'all');
    const active  = custom || generic;
    if (active) resolvedComponents.push(active);
  });

  let cashPool = payments.reduce((a, p) => a + p.amount, 0);
  let carryForward = 0;
  const ledger = [];

  BILLING_MONTHS.forEach((month, idx) => {
    const components = [];

    resolvedComponents.forEach(comp => {
      const nl = comp.name?.toLowerCase();
      if (nl.includes('transport')) return;
      let charge = false;
      if (comp.frequency === 'monthly')    charge = true;
      else if (comp.frequency === 'quarterly') charge = [4,7,10,1].includes(month.num);
      else if (comp.frequency === 'annual')    charge = month.num === 10;
      else if (comp.frequency === 'one-time')  charge = idx === 0;
      if (!charge) return;

      let discount = 0;
      if (nl.includes('tuition')) {
        if (student.siblingDiscountApplied) discount += comp.amount * 0.5;
        if (student.scholarshipPercent > 0) discount += comp.amount * (student.scholarshipPercent / 100);
      }
      const net = Math.max(0, comp.amount - discount);
      components.push({ name: comp.name, original: comp.amount, discount, net });
    });

    if (student.useTransport) {
      const route = transportRoutes.find(r => r.id === student.routeId);
      const fee = route ? route.monthlyFee : 0;
      if (fee) components.push({ name: 'Transport Fee', original: fee, discount: 0, net: fee });
    }

    const grossDue    = components.reduce((a, c) => a + c.net, 0);
    const advanceUsed = Math.min(carryForward, grossDue);
    const netPayable  = grossDue - advanceUsed;
    const cashForMonth = Math.min(cashPool, netPayable);
    cashPool -= cashForMonth;
    const paid = cashForMonth;
    const remainingDue = Math.max(0, netPayable - paid);
    const overpaid = Math.max(0, paid - netPayable);
    carryForward = (carryForward - advanceUsed) + overpaid;
    if (idx === BILLING_MONTHS.length - 1 && cashPool > 0) { carryForward += cashPool; cashPool = 0; }

    ledger.push({
      monthName: month.name,
      components,
      grossDue,
      advanceUsed,
      netPayable,
      paid,
      remainingDue,
      carryForward,
      status: remainingDue === 0 ? 'paid' : paid > 0 ? 'partial' : 'unpaid',
    });
  });

  return ledger;
}

// ─── Info Row ────────────────────────────────────────────────────────────────
function InfoRow({ icon: Icon, label, value, mono }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
      <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
        <p className={`text-sm font-semibold text-slate-800 mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</p>
      </div>
    </div>
  );
}

// ─── Status badge ────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = {
    paid:    'bg-emerald-50 text-emerald-700 border-emerald-200/60',
    partial: 'bg-amber-50  text-amber-700  border-amber-200/60',
    unpaid:  'bg-rose-50   text-rose-600   border-rose-200/60',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide border ${cfg[status]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'paid' ? 'bg-emerald-500' : status === 'partial' ? 'bg-amber-500 animate-pulse' : 'bg-rose-400'}`} />
      {status === 'unpaid' ? 'Due' : status}
    </span>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function StudentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [student,  setStudent]  = useState(null);
  const [cls,      setCls]      = useState(null);
  const [section,  setSection]  = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [feeComps, setFeeComps] = useState([]);
  const [routes,   setRoutes]   = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('general');

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const load = async () => {
    try {
      const studentRes = await api.get(`/students/${id}`);
      const s = studentRes.data;
      setStudent(s);

      const [classesRes, sectionsRes, compsRes, routesRes, paymentsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/sections'),
        api.get('/fee-components'),
        api.get('/transport-routes'),
        api.get(`/fee-payments?studentId=${id}`),
      ]);

      const foundCls = classesRes.data.find(c => c.id === s.classId);
      const foundSec = sectionsRes.data.find(sec => sec.id === s.sectionId);
      setCls(foundCls || null);
      setSection(foundSec || null);
      setFeeComps(compsRes.data || []);
      setRoutes(routesRes.data || []);
      setPayments(paymentsRes.data || []);

      if (s.familyId) {
        const sibRes = await api.get(`/students?familyId=${s.familyId}`);
        const list = (sibRes.data.students || sibRes.data || []).filter(x => x.id !== s.id);
        const clsMap = {};
        classesRes.data.forEach(c => { clsMap[c.id] = c.className; });
        const secMap = {};
        sectionsRes.data.forEach(sec => { secMap[sec.id] = sec.sectionName; });
        setSiblings(list.map(x => ({ ...x, className: clsMap[x.classId] || '—', sectionName: secMap[x.sectionId] || '—' })));
      }
    } catch {
      // silently fail — student not found handled via null check
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
    </div>
  );

  if (!student) return (
    <div className="text-center py-24 text-slate-400">
      <p className="text-lg font-semibold">Student not found</p>
      <button onClick={() => navigate('/admin/students')} className="mt-4 text-indigo-600 text-sm font-semibold hover:underline">
        ← Back to Students
      </button>
    </div>
  );

  const route     = routes.find(r => r.id === student.routeId);
  const ledger    = buildLedger(student, feeComps, routes, payments);
  const totalPaid = payments.reduce((a, p) => a + p.amount, 0);
  const totalDue  = ledger.reduce((a, m) => a + m.grossDue, 0);

  const TABS = [
    { key: 'general',  label: 'General',     icon: User },
    { key: 'siblings', label: 'Siblings',    icon: Users },
    { key: 'fees',     label: 'Fee Details', icon: DollarSign },
  ];

  const COLORS = [
    'bg-indigo-600/90','bg-amber-600/90','bg-teal-600/90',
    'bg-pink-600/90','bg-rose-600/90','bg-cyan-600/90',
  ];
  const avatarColor = COLORS[(student.name?.charCodeAt(0) || 0) % COLORS.length];

  return (
    <div className="space-y-6 max-w-4xl">

      {/* Back + Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/admin/students')}
          className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-500 transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-2xl ${avatarColor} flex items-center justify-center text-white font-black text-lg`}>
            {student.name?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <h2 className="font-geom font-bold text-xl text-slate-800">{student.name}</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {cls?.className || '—'} · {section?.sectionName || '—'}
              {student.admissionNumber && <> · <span className="font-mono">{student.admissionNumber}</span></>}
            </p>
          </div>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2">
        {totalPaid > 0 && (
          <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200/50">
            ₹{totalPaid.toLocaleString()} paid
          </span>
        )}
        {totalDue > totalPaid && (
          <span className="px-3 py-1 bg-rose-50 text-rose-600 text-xs font-bold rounded-full border border-rose-200/50">
            ₹{(totalDue - totalPaid).toLocaleString()} due
          </span>
        )}
        {student.useTransport && (
          <span className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-full border border-indigo-200/50">
            <Truck className="w-3 h-3 inline mr-1" />{route?.name || 'Transport'}
          </span>
        )}
        {student.familyId && (
          <span className="px-3 py-1 bg-purple-50 text-purple-700 text-xs font-bold rounded-full border border-purple-200/50 font-mono">
            {student.familyId}
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100/60 rounded-2xl border border-slate-200/30 max-w-fit">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all uppercase tracking-wide ${
              tab === key ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
            }`}>
            <Icon className="w-3.5 h-3.5" />
            {label}
            {key === 'siblings' && siblings.length > 0 && (
              <span className={`w-4 h-4 rounded-full text-[10px] flex items-center justify-center ${tab === key ? 'bg-white/20' : 'bg-indigo-100 text-indigo-700'}`}>
                {siblings.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── GENERAL TAB ─────────────────────────────────────────────── */}
      {tab === 'general' && (
        <div className="grid md:grid-cols-2 gap-6">

          {/* Academic Info */}
          <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm p-5">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <GraduationCap className="w-3.5 h-3.5" /> Academic Info
            </p>
            <InfoRow icon={Hash}         label="Roll Number"      value={student.rollNumber}      mono />
            <InfoRow icon={Hash}         label="Admission No."    value={student.admissionNumber} mono />
            <InfoRow icon={GraduationCap} label="Class"           value={cls?.className} />
            <InfoRow icon={Users}        label="Section"          value={section?.sectionName} />
            <InfoRow icon={Calendar}     label="Date of Birth"    value={student.dateOfBirth} />
            <InfoRow icon={Shield}       label="Aadhar Number"    value={student.aadharNumber} mono />
          </div>

          {/* Father Info */}
          <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm p-5">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <User className="w-3.5 h-3.5" /> Father
            </p>
            <InfoRow icon={User}  label="Father Name"  value={student.fatherName} />
            <InfoRow icon={Phone} label="Father Phone" value={student.fatherPhone} mono />
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 mt-4 flex items-center gap-2">
              <User className="w-3.5 h-3.5" /> Mother
            </p>
            <InfoRow icon={User}  label="Mother Name"  value={student.motherName || '—'} />
            <InfoRow icon={Phone} label="Mother Phone" value={student.motherPhone || '—'} mono />
          </div>

          {/* Address & Family */}
          <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm p-5">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Home className="w-3.5 h-3.5" /> Address & Family
            </p>
            <InfoRow icon={Home}  label="Address"   value={student.address} />
            <InfoRow icon={Users} label="Family ID" value={student.familyId} mono />
            {student.siblingDiscountApplied && (
              <div className="mt-3 px-3 py-2 bg-emerald-50 rounded-xl border border-emerald-200/50 text-xs font-semibold text-emerald-700">
                ✦ Sibling discount applied
              </div>
            )}
            {student.scholarshipPercent > 0 && (
              <div className="mt-2 px-3 py-2 bg-indigo-50 rounded-xl border border-indigo-200/50 text-xs font-semibold text-indigo-700">
                {student.scholarshipPercent}% scholarship
              </div>
            )}
          </div>

          {/* Transport */}
          <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm p-5">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Truck className="w-3.5 h-3.5" /> Transport
            </p>
            {student.useTransport ? (
              <>
                <InfoRow icon={Truck}    label="Route"        value={route?.name} />
                <InfoRow icon={Hash}     label="Route Code"   value={route?.routeCode} mono />
                <InfoRow icon={Home}     label="Pickup Point" value={student.pickupPoint} />
                <InfoRow icon={DollarSign} label="Monthly Fee" value={route ? `₹${route.monthlyFee.toLocaleString()}` : '—'} />
              </>
            ) : (
              <p className="text-sm text-slate-400 italic">Not using school transport</p>
            )}
          </div>

          {/* Login */}
          <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm p-5 md:col-span-2">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <CreditCard className="w-3.5 h-3.5" /> Login Credentials
            </p>
            <InfoRow icon={CreditCard} label="Student Login ID" value={student.loginId} mono />
          </div>
        </div>
      )}

      {/* ── SIBLINGS TAB ────────────────────────────────────────────── */}
      {tab === 'siblings' && (
        <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm p-6">
          {!student.familyId ? (
            <div className="text-center py-12 text-slate-400">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-semibold">No family ID assigned</p>
              <p className="text-xs mt-1">Link a family ID to this student to see siblings</p>
            </div>
          ) : siblings.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-semibold">No other siblings found</p>
              <p className="text-xs mt-1 font-mono">{student.familyId}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">
                {siblings.length} sibling{siblings.length !== 1 ? 's' : ''} in family <span className="font-mono">{student.familyId}</span>
              </p>
              {siblings.map((sib, i) => (
                <div key={sib.id}
                  onClick={() => navigate(`/admin/students/${sib.id}`)}
                  className="flex items-center justify-between p-4 rounded-xl border border-slate-200/60 hover:border-indigo-200 hover:bg-indigo-50/30 cursor-pointer transition-all">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl ${COLORS[i % COLORS.length]} flex items-center justify-center text-white font-bold text-sm`}>
                      {sib.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-800">{sib.name}</p>
                      <p className="text-xs text-slate-400">{sib.className} · {sib.sectionName}</p>
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-400">
                    {sib.admissionNumber && <p className="font-mono">{sib.admissionNumber}</p>}
                    {sib.siblingDiscountApplied && <span className="text-emerald-600 font-semibold">✦ Discount</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── FEE DETAILS TAB ─────────────────────────────────────────── */}
      {tab === 'fees' && (
        <div className="space-y-4">

          {/* Summary row */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total Billed', value: `₹${totalDue.toLocaleString()}`, color: 'text-slate-800' },
              { label: 'Total Paid',   value: `₹${totalPaid.toLocaleString()}`, color: 'text-emerald-600' },
              { label: 'Balance Due',  value: `₹${Math.max(0, totalDue - totalPaid).toLocaleString()}`, color: 'text-rose-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-2xl border border-slate-200/40 shadow-sm p-5 text-center">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
                <p className={`text-2xl font-black mt-1 font-mono ${color}`}>{value}</p>
              </div>
            ))}
          </div>

          {/* Monthly ledger */}
          <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <p className="text-sm font-bold text-slate-700">Monthly Ledger</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {['Month', 'Components', 'Gross Due', 'Advance Used', 'Net Payable', 'Paid', 'Balance', 'Status'].map(h => (
                      <th key={h} className="px-4 py-3 text-left font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ledger.map(month => (
                    <tr key={month.monthName} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3 font-semibold text-slate-700 whitespace-nowrap">{month.monthName}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {month.components.map((c, i) => (
                            <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[10px] font-semibold border border-slate-200/50">
                              {c.name.replace(' Fee', '')} ₹{c.net.toLocaleString()}
                              {c.discount > 0 && <span className="text-emerald-600 ml-0.5">✦</span>}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono font-semibold text-slate-700">₹{month.grossDue.toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono text-indigo-600">{month.advanceUsed > 0 ? `₹${month.advanceUsed.toLocaleString()}` : '—'}</td>
                      <td className="px-4 py-3 font-mono font-bold text-slate-800">₹{month.netPayable.toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono text-emerald-600">{month.paid > 0 ? `₹${month.paid.toLocaleString()}` : '—'}</td>
                      <td className="px-4 py-3 font-mono text-rose-600">{month.remainingDue > 0 ? `₹${month.remainingDue.toLocaleString()}` : '—'}</td>
                      <td className="px-4 py-3"><StatusBadge status={month.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment history */}
          {payments.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <p className="text-sm font-bold text-slate-700">Payment History</p>
              </div>
              <div className="divide-y divide-slate-100">
                {payments.map((p, i) => (
                  <div key={p.id || i} className="flex items-center justify-between px-6 py-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-700">₹{p.amount.toLocaleString()}</p>
                      <p className="text-xs text-slate-400">{p.paymentDate ? new Date(p.paymentDate).toLocaleDateString('en-IN') : '—'} · {p.method || 'Cash'}</p>
                    </div>
                    {p.remarks && <p className="text-xs text-slate-400 italic">{p.remarks}</p>}
                    <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200/50">Received</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
